// Google Analytics 4 Integration for Common Ground
import { getUserInfo } from './userInfo'

// Initialize Google Analytics
export const initGA = () => {
  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID

  if (!measurementId) {
    console.warn('Google Analytics Measurement ID not found')
    return
  }

  // Load Google Analytics script
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
  document.head.appendChild(script)

  // Initialize gtag
  window.dataLayer = window.dataLayer || []
  function gtag() {
    window.dataLayer.push(arguments)
  }
  window.gtag = gtag

  gtag('js', new Date())
  gtag('config', measurementId)
}

// Track page views
export const trackPageView = (page_title, page_location) => {
  if (typeof window.gtag !== 'function') return

  window.gtag('event', 'page_view', {
    page_title,
    page_location
  })
}

// Game-specific events
export const trackGameEvent = (action, parameters = {}) => {
  if (typeof window.gtag !== 'function') return

  window.gtag('event', action, {
    event_category: 'game',
    ...parameters
  })
}

// Specific game tracking functions
export const analytics = {
  // Identity & Journey
  identityCreated: (identity, recoveryKey) => {
    const userInfo = getUserInfo()
    trackGameEvent('identity_created', {
      identity_type: identity,
      method: 'new_creation',
      user_language: userInfo.language.primary,
      user_language_code: userInfo.language.primaryCode,
      user_timezone: userInfo.timezone.id,
      user_timezone_offset: userInfo.timezone.offset
    })
  },

  identityRecovered: (identity, recoveryKey) => {
    const userInfo = getUserInfo()
    trackGameEvent('identity_recovered', {
      identity_type: identity,
      method: 'recovery_key',
      user_language: userInfo.language.primary,
      user_language_code: userInfo.language.primaryCode,
      user_timezone: userInfo.timezone.id,
      user_timezone_offset: userInfo.timezone.offset
    })
  },

  // Lobby & Matchmaking
  lobbyJoined: (soulsHelped, commonGroundPercentage) => {
    trackGameEvent('lobby_joined', {
      souls_helped: soulsHelped || 0,
      common_ground_percentage: commonGroundPercentage || 0
    })
  },

  matchRequested: (targetIdentity, userSoulsHelped) => {
    trackGameEvent('match_requested', {
      target_identity: targetIdentity,
      user_souls_helped: userSoulsHelped || 0
    })
  },

  matchAccepted: (partnerIdentity, level) => {
    trackGameEvent('match_accepted', {
      partner_identity: partnerIdentity,
      partnership_level: level || 1
    })
  },

  matchDeclined: (partnerIdentity) => {
    trackGameEvent('match_declined', {
      partner_identity: partnerIdentity
    })
  },

  // Gameplay
  roundStarted: (level, roundNumber, partnerIdentity) => {
    trackGameEvent('round_started', {
      partnership_level: level,
      round_number: roundNumber,
      partner_identity: partnerIdentity
    })
  },

  answerSubmitted: (questionIndex, answer, timeRemaining, level) => {
    trackGameEvent('answer_submitted', {
      question_index: questionIndex,
      time_remaining: timeRemaining,
      partnership_level: level
    })
  },

  roundCompleted: (level, roundNumber, matches, success, partnerIdentity) => {
    trackGameEvent('round_completed', {
      partnership_level: level,
      round_number: roundNumber,
      matches_found: matches,
      round_success: success,
      partner_identity: partnerIdentity
    })
  },

  // Outcomes
  partnershipSuccess: (level, totalMatches, partnerIdentity, escaped = false) => {
    trackGameEvent('partnership_success', {
      partnership_level: level,
      total_matches: totalMatches,
      partner_identity: partnerIdentity,
      escaped_purgatory: escaped
    })
  },

  partnershipFailed: (level, totalMatches, partnerIdentity, reason = 'insufficient_matches') => {
    trackGameEvent('partnership_failed', {
      partnership_level: level,
      total_matches: totalMatches,
      partner_identity: partnerIdentity,
      failure_reason: reason
    })
  },

  escapeAchieved: (partnerIdentity, totalRounds, totalMatches, soulsHelped) => {
    trackGameEvent('escape_achieved', {
      partner_identity: partnerIdentity,
      total_rounds: totalRounds,
      total_matches: totalMatches,
      souls_helped: soulsHelped,
      value: 1 // High-value event
    })
  },

  // User Actions
  recoveryKeyDownloaded: (identity) => {
    trackGameEvent('recovery_key_downloaded', {
      identity_type: identity
    })
  },

  analyticsViewed: (fromPage) => {
    trackGameEvent('analytics_viewed', {
      source_page: fromPage
    })
  },

  chatStarted: (partnerIdentity, level) => {
    trackGameEvent('chat_started', {
      partner_identity: partnerIdentity,
      partnership_level: level
    })
  },

  leftLobby: (soulsHelped, commonGroundPercentage) => {
    trackGameEvent('lobby_left', {
      souls_helped: soulsHelped || 0,
      common_ground_percentage: commonGroundPercentage || 0
    })
  },

  // Errors & Technical
  connectionError: (errorType, errorMessage) => {
    trackGameEvent('connection_error', {
      error_type: errorType,
      error_message: errorMessage
    })
  },

  sessionExpired: (identity, timeInSession) => {
    trackGameEvent('session_expired', {
      identity_type: identity,
      session_duration: timeInSession
    })
  }
}

export default analytics
